import { useCallback, useEffect, useState, type RefObject, type UIEvent } from 'react'

/**
 * Seuil de tolerance (px) pour considerer qu'on est "en bas" -- pattern stick-to-bottom
 * standard. Sans marge, l'autoscroll ne se rearmerait jamais a cause d'arrondis sous-pixel
 * du navigateur (zoom, layout fractionnaire). Valeur reprise telle quelle du fix Terminal
 * (journal BDD #26277), desormais commune aux deux vues (TerminalView, MessageList).
 */
const BOTTOM_THRESHOLD_PX = 32

/**
 * Pattern stick-to-bottom partage entre TerminalView et MessageList (extrait du fix Terminal
 * #26277, journal BDD #26422 -- stick-to-bottom Discussion). Etat LOCAL (pas de store Zustand,
 * contrairement a status.store / send-queue.store / voice-recording.store) : le scroll n'a
 * aucun processus externe a preserver (pas de MediaRecorder equivalent), c'est une position de
 * vue pure, entierement re-derivable au montage -- decision validee Claude PHASE3 du fix
 * Terminal, reconduite ici.
 *
 * NE GERE PAS l'effet "contenu change -> scroll si pinne" : ses dependances (quels changements
 * comptent comme "nouveau contenu") et son comportement de scroll divergent reellement entre
 * les vues consommatrices -- reste dans chaque vue, qui lit `isPinnedToBottom` et appelle
 * elle-meme `endRef.current?.scrollIntoView(...)` sous garde. Une deps array transmise
 * dynamiquement a ce hook serait un piege React non verifiable par exhaustive-deps, pour ne
 * factoriser que 2 lignes (journal BDD #26422, decision de conception documentee).
 */
export function useStickToBottom(endRef: RefObject<HTMLDivElement | null>, resetSignal?: number) {
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true)

  /**
   * Handler de scroll du conteneur -- calcule si on est "en bas" (dans la tolerance
   * BOTTOM_THRESHOLD_PX) pour armer/desarmer l'autoscroll. Travaille directement sur
   * e.currentTarget (l'element auquel ce handler est attache) -- pas besoin d'une ref separee
   * sur le conteneur.
   *
   * Garde `e.target !== e.currentTarget` : le scroll interne d'un bloc (sortie d'outil
   * terminal, bloc de code markdown) ne doit JAMAIS etre confondu avec le scroll de ce
   * conteneur. L'event DOM natif 'scroll' ne bubble pas nativement (bubbles:false) donc ce
   * handler ne devrait de toute facon jamais etre invoque pour un scroll enfant -- verifie
   * empiriquement (harnais isole, journal BDD #26277) -- mais la garde ferme le cas
   * explicitement plutot que de compter sur cette propriete implicite.
   */
  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return
    const el = e.currentTarget
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    setIsPinnedToBottom(distance <= BOTTOM_THRESHOLD_PX)
  }, [])

  /** Repin force + retour instantane en bas -- bouton "aller en bas" ET signal externe. */
  const scrollToBottom = useCallback(() => {
    setIsPinnedToBottom(true)
    endRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior })
  }, [endRef])

  // Repin a un signal externe (ex: envoi d'un message) -- reutilise le canal existant plutot
  // que d'en creer un (journal BDD #26422). Se declenche aussi au tout premier montage
  // (resetSignal initial, souvent 0/undefined) : sans consequence, isPinnedToBottom est deja
  // true a ce moment (etat par defaut ci-dessus).
  useEffect(() => {
    scrollToBottom()
  }, [resetSignal, scrollToBottom])

  return { isPinnedToBottom, handleScroll, scrollToBottom }
}
